import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import { ShareResultsButton } from '../components/ShareResultsCard';
import { 
  Leaf, ArrowRight, Clock, Pill, AlertTriangle, CheckCircle2,
  Moon, Zap, Shield, Brain, Heart, Scale, Utensils, 
  Bone, Sparkles, Smile, Loader2, LayoutDashboard, Info,
  Calendar, Coffee, Sun, Sunset, Download, FileText, Share2,
  Mail, Link, Copy, Bell, ShoppingCart, ExternalLink, XCircle,
  Check, Square, CheckSquare, ListChecks, Printer, Save, BookmarkCheck,
  Lightbulb, Activity, Eye, BookOpen, FlaskConical, GraduationCap,
  Cigarette, Wine, Package, Plus, TrendingUp, TrendingDown, RefreshCw, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Amazon regional domains mapping based on country
const AMAZON_REGIONS = {
  // Europe
  'DE': 'amazon.de', 'AT': 'amazon.de', 'CH': 'amazon.de', 'HU': 'amazon.de', 'PL': 'amazon.de', 'CZ': 'amazon.de', 'SK': 'amazon.de',
  'GB': 'amazon.co.uk', 'IE': 'amazon.co.uk',
  'FR': 'amazon.fr', 'BE': 'amazon.fr', 'LU': 'amazon.fr',
  'IT': 'amazon.it', 'VA': 'amazon.it', 'SM': 'amazon.it',
  'ES': 'amazon.es', 'PT': 'amazon.es', 'AD': 'amazon.es',
  'NL': 'amazon.nl',
  'SE': 'amazon.se', 'NO': 'amazon.se', 'DK': 'amazon.se', 'FI': 'amazon.se', 'IS': 'amazon.se',
  // Asia-Pacific
  'JP': 'amazon.co.jp',
  'AU': 'amazon.com.au', 'NZ': 'amazon.com.au',
  'IN': 'amazon.in',
  'SG': 'amazon.sg', 'MY': 'amazon.sg', 'TH': 'amazon.sg', 'VN': 'amazon.sg', 'PH': 'amazon.sg', 'ID': 'amazon.sg',
  'AE': 'amazon.ae', 'SA': 'amazon.ae', 'EG': 'amazon.ae', 'KW': 'amazon.ae', 'QA': 'amazon.ae', 'BH': 'amazon.ae', 'OM': 'amazon.ae',
  // Americas
  'CA': 'amazon.ca',
  'MX': 'amazon.com.mx',
  'BR': 'amazon.com.br',
  // Default to US
  'US': 'amazon.com'
};

// Get Amazon URL based on user's country
function getAmazonUrl(supplementName, userCountry) {
  const countryCode = userCountry?.toUpperCase() || 'US';
  const amazonDomain = AMAZON_REGIONS[countryCode] || 'amazon.com';
  return `https://www.${amazonDomain}/s?k=${encodeURIComponent(supplementName)}`;
}

const categoryIcons = {
  sleep: Moon,
  energy: Zap,
  immunity: Shield,
  stress: Brain,
  weight: Scale,
  heart: Heart,
  digestion: Utensils,
  joints: Bone,
  hair_skin: Sparkles,
  brain_focus: Brain,
  mood: Smile,
  current: CheckCircle2,
  vision: Eye,
  smoking_cessation: Cigarette,
  alcohol_cessation: Wine
};

const categoryColors = {
  sleep: 'bg-indigo-100 text-indigo-600 border-indigo-200',
  energy: 'bg-amber-100 text-amber-600 border-amber-200',
  immunity: 'bg-green-100 text-green-600 border-green-200',
  stress: 'bg-purple-100 text-purple-600 border-purple-200',
  weight: 'bg-rose-100 text-rose-600 border-rose-200',
  heart: 'bg-red-100 text-red-600 border-red-200',
  digestion: 'bg-orange-100 text-orange-600 border-orange-200',
  joints: 'bg-cyan-100 text-cyan-600 border-cyan-200',
  hair_skin: 'bg-pink-100 text-pink-600 border-pink-200',
  brain_focus: 'bg-violet-100 text-violet-600 border-violet-200',
  mood: 'bg-yellow-100 text-yellow-600 border-yellow-200',
  current: 'bg-emerald-100 text-emerald-600 border-emerald-200',
  vision: 'bg-sky-100 text-sky-600 border-sky-200',
  smoking_cessation: 'bg-teal-100 text-teal-600 border-teal-200',
  alcohol_cessation: 'bg-teal-100 text-teal-600 border-teal-200'
};

const priorityColors = {
  essential: 'bg-primary text-white',
  recommended: 'bg-secondary/20 text-secondary',
  optional: 'bg-muted text-muted-foreground',
  current: 'bg-emerald-500 text-white'
};

const timingIcons = {
  morning: Sun,
  afternoon: Coffee,
  evening: Sunset,
  bedtime: Moon,
  default: Clock
};

function getTimingIcon(timing) {
  const t = timing.toLowerCase();
  if (t.includes('morning') || t.includes('breakfast')) return Sun;
  if (t.includes('afternoon') || t.includes('lunch')) return Coffee;
  if (t.includes('evening') || t.includes('dinner')) return Sunset;
  if (t.includes('bed') || t.includes('night')) return Moon;
  return Clock;
}

// Parse AI recommendation into separate sections
function parseAIRecommendation(text) {
  if (!text) return { healthInsights: '', lifestyleTips: '' };
  
  const lines = text.split('\n');
  let healthInsights = [];
  let lifestyleTips = [];
  let currentSection = 'health';
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    
    // Check for section headers
    if (trimmed.toLowerCase().includes('lifestyle tips') || trimmed.toLowerCase().includes('lifestyle:')) {
      currentSection = 'lifestyle';
      continue;
    }
    if (trimmed.toLowerCase().includes('supplement recommendation')) {
      currentSection = 'skip'; // Skip this section as it's redundant
      continue;
    }
    if (trimmed.toLowerCase().includes('key health') || trimmed.toLowerCase().includes('health focus')) {
      currentSection = 'health';
      continue;
    }
    
    // Add line to appropriate section
    if (currentSection === 'health') {
      healthInsights.push(trimmed);
    } else if (currentSection === 'lifestyle') {
      lifestyleTips.push(trimmed);
    }
  }
  
  return {
    healthInsights: healthInsights.join('\n'),
    lifestyleTips: lifestyleTips.join('\n')
  };
}

export default function ResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader, loading: authLoading, token } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [creatingShareLink, setCreatingShareLink] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ provider_email: '', provider_name: '', message: '' });
  const [affiliateSettings, setAffiliateSettings] = useState(null);
  const [affiliateLinks, setAffiliateLinks] = useState({});
  
  // Protocol feature state (formerly schedule)
  const [selectedSupplements, setSelectedSupplements] = useState({});
  const [showProtocol, setShowProtocol] = useState(false);
  const [generatedProtocol, setGeneratedProtocol] = useState(null);
  const [generatingProtocol, setGeneratingProtocol] = useState(false);
  const [downloadingProtocolPdf, setDownloadingProtocolPdf] = useState(false);
  const [savingProtocol, setSavingProtocol] = useState(false);
  const [protocolSaved, setProtocolSaved] = useState(false);
  const [creatingReminders, setCreatingReminders] = useState(false);
  const [remindersCreated, setRemindersCreated] = useState(false);
  const [hasExistingReminders, setHasExistingReminders] = useState(false);
  const [showCurrentMedsProtocol, setShowCurrentMedsProtocol] = useState(false);
  const [currentMedsProtocol, setCurrentMedsProtocol] = useState(null);
  const [generatingCurrentProtocol, setGeneratingCurrentProtocol] = useState(false);
  const [supplementResearch, setSupplementResearch] = useState({});
  const [loadingResearch, setLoadingResearch] = useState({});
  const [expandedResearch, setExpandedResearch] = useState({});
  const [userCountry, setUserCountry] = useState('US');
  const [addingToStack, setAddingToStack] = useState({});
  const [addedToStack, setAddedToStack] = useState({});
  const [planComparison, setPlanComparison] = useState(null);
  const [dynamicRetailers, setDynamicRetailers] = useState([]);

  // Detect user's country for regional Amazon links
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (data.country_code) {
          setUserCountry(data.country_code);
        }
      } catch (error) {
        console.log('Could not detect country, defaulting to US');
      }
    };
    detectCountry();
  }, []);

  // Country answers in the assessment are stored as full names ("United Kingdom"),
  // but our retailer chooser keys off ISO codes ("GB"). Build a lightweight map so
  // a user whose IP is elsewhere still gets the right geo-targeted retailer.
  const COUNTRY_NAME_TO_CODE = {
    'united kingdom': 'GB', 'uk': 'GB', 'great britain': 'GB',
    'united states': 'US', 'usa': 'US',
    'canada': 'CA', 'australia': 'AU', 'new zealand': 'NZ',
    'germany': 'DE', 'france': 'FR', 'italy': 'IT', 'spain': 'ES', 'netherlands': 'NL',
    'india': 'IN', 'singapore': 'SG', 'japan': 'JP', 'mexico': 'MX', 'brazil': 'BR',
  };

  const assessmentCountryName = assessment?.answers?.find?.(a => a.question_id === 'country')?.answer;
  const effectiveCountry = (assessmentCountryName && COUNTRY_NAME_TO_CODE[String(assessmentCountryName).toLowerCase()]) || userCountry;

  // Fetch the admin-configurable retailer registry for the user's geo. Whenever
  // the effective country changes (IP detected or quiz country resolved), we
  // re-fetch so geo-targeted retailers (like Focus UK) appear correctly.
  useEffect(() => {
    const fetchRetailers = async () => {
      try {
        const res = await axios.get(`${API}/retailers/for-country`, {
          params: { country: effectiveCountry || '' }
        });
        setDynamicRetailers(res.data.retailers || []);
      } catch (err) {
        console.log('Could not load retailers registry, falling back to defaults');
        setDynamicRetailers([]);
      }
    };
    fetchRetailers();
  }, [effectiveCountry]);

  // Fetch research evidence for a supplement
  const fetchResearch = async (supplementName) => {
    if (supplementResearch[supplementName] !== undefined) {
      // Already fetched, just toggle visibility
      setExpandedResearch(prev => ({ ...prev, [supplementName]: !prev[supplementName] }));
      return;
    }

    setLoadingResearch(prev => ({ ...prev, [supplementName]: true }));
    try {
      const response = await axios.get(`${API}/research/${encodeURIComponent(supplementName)}`);
      setSupplementResearch(prev => ({ 
        ...prev, 
        [supplementName]: response.data.found ? response.data.research : null 
      }));
      setExpandedResearch(prev => ({ ...prev, [supplementName]: true }));
    } catch (error) {
      console.error('Error fetching research:', error);
      setSupplementResearch(prev => ({ ...prev, [supplementName]: null }));
    } finally {
      setLoadingResearch(prev => ({ ...prev, [supplementName]: false }));
    }
  };

  // Get evidence strength badge color
  const getEvidenceColor = (strength) => {
    switch (strength) {
      case 'strong': return 'bg-green-100 text-green-700 border-green-300';
      case 'moderate': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'emerging': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  // Get evidence strength dots
  const getEvidenceDots = (strength) => {
    switch (strength) {
      case 'strong': return '●●●○';
      case 'moderate': return '●●○○';
      case 'emerging': return '●○○○';
      default: return '○○○○';
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/results/' + id);
    }
  }, [user, authLoading, navigate, id]);

  useEffect(() => {
    const fetchAssessment = async () => {
      if (!user) return;
      
      try {
        const response = await axios.get(`${API}/assessment/${id}`, {
          headers: getAuthHeader()
        });
        setAssessment(response.data);
        
        // Fetch affiliate settings
        const affiliateRes = await axios.get(`${API}/affiliate/settings`);
        setAffiliateSettings(affiliateRes.data);

        // Fetch plan comparison if user has multiple assessments
        try {
          const compareRes = await axios.get(`${API}/assessments/compare`, { headers: getAuthHeader() });
          if (compareRes.data.comparison && compareRes.data.comparison.latest.id === id) {
            setPlanComparison(compareRes.data.comparison);
          }
        } catch (e) {
          console.warn('Plan comparison fetch failed (non-critical):', e?.message);
        }

        // Check if user already has reminders
        try {
          const remRes = await axios.get(`${API}/reminders`, { headers: getAuthHeader() });
          if (remRes.data && remRes.data.length > 0) {
            setHasExistingReminders(true);
          }
        } catch (e) {
          console.warn('Reminders check failed (non-critical):', e?.message);
        }
      } catch (error) {
        console.error('Error fetching assessment:', error);
        console.error('Error details:', error.response?.data || error.message);
        toast.error('Failed to load results');
        // Don't navigate away - show error state instead
        setAssessment({ _error: true });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchAssessment();
    }
  }, [id, user, getAuthHeader, navigate]);

  // DO NOT pre-select supplements - user should choose what to add
  // This is intentionally left empty - all checkboxes start unchecked

  // Auto-generate "Current Protocol" when assessment loads (only for current meds/supps)
  useEffect(() => {
    const autoGenerateCurrentProtocol = async () => {
      if (!assessment || !user || generatedProtocol) return;
      
      // Only auto-generate if user has current medications or supplements
      const hasCurrent = (assessment.medications?.length > 0) || (assessment.current_supplements?.length > 0);
      if (!hasCurrent) return;

      try {
        const response = await axios.post(
          `${API}/assessment/${id}/generate-schedule`,
          {
            selected_supplements: assessment.current_supplements || [], // Only current supplements
            current_medications: assessment.medications || [],
            current_supplements: assessment.current_supplements || []
          },
          { headers: getAuthHeader() }
        );
        setGeneratedProtocol(response.data.schedule);
      } catch (error) {
        console.error('Error auto-generating protocol:', error);
      }
    };

    // Auto-generate protocol for current meds/supps only
    if (assessment && !generatedProtocol) {
      autoGenerateCurrentProtocol();
    }
  }, [assessment, user, id, getAuthHeader, generatedProtocol]);

  // Handle Escape key to close protocol modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showProtocol) {
        setShowProtocol(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showProtocol]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      // Use the new full-pdf endpoint that includes protocols
      const response = await axios.post(
        `${API}/assessment/${id}/full-pdf`,
        {
          current_protocol: generatedProtocol || null,  // Current protocol (auto-generated from current meds/supps)
          new_protocol: showProtocol ? generatedProtocol : null  // New protocol (if user generated one)
        },
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vitala_full_report_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded! Share it with your healthcare provider.');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleEmailToProvider = async () => {
    if (!emailForm.provider_email) {
      toast.error('Please enter provider email');
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/assessment/${id}/email-provider`, emailForm, {
        headers: getAuthHeader()
      });
      toast.success(`Report sent to ${emailForm.provider_email}`);
      setShowEmailDialog(false);
      setEmailForm({ provider_email: '', provider_name: '', message: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCreateShareLink = async () => {
    setCreatingShareLink(true);
    try {
      const response = await axios.post(`${API}/assessment/${id}/share`, {}, {
        headers: getAuthHeader()
      });
      setShareLink(response.data);
      navigator.clipboard.writeText(response.data.share_url);
      toast.success('Share link copied to clipboard!');
    } catch (error) {
      console.error('Error creating share link:', error);
      toast.error('Failed to create share link');
    } finally {
      setCreatingShareLink(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex flex-col items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="relative w-20 h-20 mx-auto mb-6">
            <Loader2 className="w-20 h-20 text-primary animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary/60" />
            </div>
          </div>
          
          <h2 className="text-xl font-semibold text-[#1A1A1A] mb-2">Creating Your Personalized Protocol</h2>
          
          {/* Animated Progress Steps */}
          <div className="max-w-sm mx-auto mb-6 space-y-3">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-3 text-left p-2 bg-white rounded-lg border border-border"
            >
              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-sm text-muted-foreground">Analyzing your health profile</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="flex items-center gap-3 text-left p-2 bg-white rounded-lg border border-border"
            >
              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-sm text-muted-foreground">Checking medication interactions</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.4 }}
              className="flex items-center gap-3 text-left p-2 bg-white rounded-lg border border-primary/30"
            >
              <Loader2 className="w-5 h-5 text-primary animate-spin flex-shrink-0" />
              <span className="text-sm text-primary font-medium">Generating personalized recommendations...</span>
            </motion.div>
          </div>
          
          {/* AI Processing Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 text-left">
                <strong>Reminder:</strong> These are AI-generated suggestions for educational purposes only. 
                Always consult a healthcare professional before starting any supplement regimen.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (!assessment) {
    return null;
  }

  // Guard against missing data or fetch error - show error state instead of crashing
  if (assessment._error || !assessment.health_scores || !assessment.recommended_supplements) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex flex-col items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#1A1A1A] mb-2">
            {assessment._error ? 'Failed to Load Results' : 'Assessment Data Incomplete'}
          </h2>
          <p className="text-muted-foreground mb-6">
            {assessment._error 
              ? 'There was an error loading your assessment. Please try again.'
              : 'Your assessment results are still being processed or there was an issue generating your recommendations.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Refresh Page
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 border border-border rounded-lg hover:bg-gray-50"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get user's gender from answers to filter out irrelevant categories
  const userGender = assessment.answers?.find(a => a.question_id === 'gender')?.answer;
  const isMale = userGender?.toLowerCase() === 'male';
  const femaleOnlyCategories = ['menopause', 'fertility'];
  
  // Filter out female-only categories for males and categories with 100% score (likely N/A)
  const sortedScores = Object.entries(assessment.health_scores || {})
    .filter(([category, score]) => {
      // If male, hide menopause and fertility
      if (isMale && femaleOnlyCategories.includes(category)) {
        return false;
      }
      // Hide categories where user likely selected "Not Applicable" (100% score on irrelevant category)
      if (score === 100 && femaleOnlyCategories.includes(category)) {
        return false;
      }
      return true;
    })
    .sort(([, a], [, b]) => b - a);
  
  // Sort supplements by priority: Essential > Recommended > Optional
  const priorityOrder = { 'essential': 0, 'recommended': 1, 'optional': 2, 'current': 3 };
  
  // Create unified supplement list: merge current supplements with recommendations
  const unifiedSupplements = (() => {
    const recommendations = [...(assessment.recommended_supplements || [])];
    const currentSupps = assessment.current_supplements || [];
    const currentSuppsLower = currentSupps.map(s => s.toLowerCase());
    
    // Normalize supplement name for comparison
    const normalizeName = (name) => {
      return name.toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/&/g, 'and')
        .replace(/\bwith\b/g, 'and')
        .replace(/\bplus\b/g, 'and')
        .replace(/\+/g, 'and')
        .replace(/vitamin\s*/gi, 'vit')
        .replace(/coenzyme\s*q\s*10/gi, 'coq10')
        .replace(/co-?q-?10/gi, 'coq10')
        .replace(/omega-?3/gi, 'omega3')
        .replace(/fish\s*oil/gi, 'omega3')
        .replace(/b-?12/gi, 'b12')
        .replace(/d-?3/gi, 'd3')
        .trim();
    };
    
    // Helper to check if user is already taking something similar
    const isAlreadyTaking = (recName) => {
      const recNorm = normalizeName(recName);
      
      return currentSuppsLower.some(cs => {
        const csNorm = normalizeName(cs);
        
        // Exact match after normalization
        if (csNorm === recNorm) return true;
        
        // IMPORTANT: For combination supplements (containing "+", "&", or "and"), require more specific matching
        const recHasCombo = recName.toLowerCase().includes('+') || recName.toLowerCase().includes(' and ') || recName.toLowerCase().includes('&') || recName.toLowerCase().includes(' with ');
        const csHasCombo = cs.toLowerCase().includes('+') || cs.toLowerCase().includes(' and ') || cs.toLowerCase().includes('&') || cs.toLowerCase().includes(' with ');
        
        if (recHasCombo || csHasCombo) {
          // For combo supplements, require BOTH main ingredients to match
          // e.g., "Calcium + Vitamin D3" should only match if user has BOTH calcium AND vitamin d
          const recIngredients = recNorm.split(/\s+/).filter(w => w.length > 2 && !['and'].includes(w));
          const csIngredients = csNorm.split(/\s+/).filter(w => w.length > 2 && !['and'].includes(w));
          
          // Check if it's a combo like "Calcium + D3" - if so, user must have calcium component
          if (recNorm.includes('calcium') && !csNorm.includes('calcium')) {
            return false; // User doesn't have calcium, so not a match
          }
          
          // For combos, need substantial word overlap (at least 70%)
          const matchingWords = recIngredients.filter(w => csIngredients.some(cw => cw.includes(w) || w.includes(cw)));
          const minWords = Math.min(recIngredients.length, csIngredients.length);
          if (matchingWords.length >= minWords * 0.7 && minWords >= 2) return true;
          
          // If not a strong match, don't allow contains-based matching for combos
          return false;
        }
        
        // For non-combo supplements, use contains matching
        if (csNorm.includes(recNorm) || recNorm.includes(csNorm)) return true;
        
        // Specific matches
        // CoQ10 variations
        if (csNorm.includes('coq10') && recNorm.includes('coq10')) return true;
        
        // B12 variations
        if (csNorm.includes('b12') && recNorm.includes('b12')) return true;
        
        // Vitamin D3 - must match D3 specifically
        if (csNorm.includes('d3') && recNorm.includes('d3')) return true;
        
        // Omega-3 / Fish Oil
        if (csNorm.includes('omega3') && recNorm.includes('omega3')) return true;
        
        // Magnesium - any type matches any type
        if (csNorm.includes('magnesium') && recNorm.includes('magnesium')) return true;
        
        return false;
      });
    };
    
    // Filter recommendations: EXCLUDE ones user is already taking AND female-only for males
    const newRecommendations = recommendations.filter(supp => {
      if (isAlreadyTaking(supp.name)) return false;
      if (isMale && femaleOnlyCategories.includes(supp.category)) return false;
      return true;
    });
    
    // Current supplements user is already taking - show as "Continue to Take"
    const continueToTake = currentSupps.map(suppName => {
      // Try to find matching recommendation to get dosage/timing details
      const matchingRec = recommendations.find(rec => isAlreadyTaking(rec.name) && (
        normalizeName(rec.name).includes(normalizeName(suppName)) ||
        normalizeName(suppName).includes(normalizeName(rec.name))
      ));
      
      return {
        name: suppName,
        priority: 'current',
        category: 'current',
        timing: matchingRec?.timing || 'As directed',
        dosage: matchingRec?.dosage || 'Current dose',
        benefits: matchingRec?.benefits || 'Already part of your regimen',
        how_to_take: matchingRec?.how_to_take || 'Continue as directed',
        duration: 'Ongoing',
        what_to_look_for: matchingRec?.what_to_look_for || 'Maintain consistency',
        isCurrentSupplement: true
      };
    });
    
    // Combine: NEW recommendations first, then "Continue to Take"
    return [...newRecommendations, ...continueToTake];
  })();
  
  // Sort: Essential first, then Recommended, then "Continue to Take" (current)
  const sortedSupplements = unifiedSupplements.sort((a, b) => {
    const priorityA = priorityOrder[a.priority?.toLowerCase()] ?? 2;
    const priorityB = priorityOrder[b.priority?.toLowerCase()] ?? 2;
    return priorityA - priorityB;
  });

  // Separate cessation supplements from regular ones
  const cessationCategories = ['smoking_cessation', 'alcohol_cessation'];
  const cessationSupplements = sortedSupplements.filter(s => cessationCategories.includes(s.category));
  const regularSupplements = sortedSupplements.filter(s => !cessationCategories.includes(s.category));

  const hasInteractions = assessment.drug_interactions && assessment.drug_interactions.length > 0;

  // Toggle supplement selection
  const toggleSupplementSelection = (supplementName) => {
    setSelectedSupplements(prev => ({
      ...prev,
      [supplementName]: !prev[supplementName]
    }));
  };

  // Select all supplements
  const selectAllSupplements = () => {
    const allSelected = {};
    sortedSupplements.forEach(s => {
      allSelected[s.name] = true;
    });
    setSelectedSupplements(allSelected);
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedSupplements({});
  };

  // Generate protocol for current medications only
  const generateCurrentMedsProtocol = async () => {
    const currentMeds = assessment.medications || [];
    const currentSupps = assessment.current_supplements || [];
    
    if (currentMeds.length === 0 && currentSupps.length === 0) {
      toast.error('No current medications or supplements to generate protocol');
      return;
    }

    setGeneratingCurrentProtocol(true);
    
    try {
      const response = await axios.post(
        `${API}/assessment/${id}/generate-schedule`,
        {
          selected_supplements: currentSupps,
          current_medications: currentMeds,
          current_supplements: [],
          protocol_type: 'current_only'
        },
        { headers: getAuthHeader() }
      );
      
      setCurrentMedsProtocol(response.data.schedule);
      setShowCurrentMedsProtocol(true);
      toast.success('Current medications protocol generated!');
    } catch (error) {
      console.error('Error generating current meds protocol:', error);
      toast.error('Failed to generate protocol');
    } finally {
      setGeneratingCurrentProtocol(false);
    }
  };

  // Generate optimized protocol based on selected supplements
  const generateProtocol = async () => {
    const selected = sortedSupplements.filter(s => selectedSupplements[s.name]);
    if (selected.length === 0) {
      toast.error('Please select at least one supplement');
      return;
    }

    setGeneratingProtocol(true);
    
    try {
      const response = await axios.post(
        `${API}/assessment/${id}/generate-schedule`,
        {
          selected_supplements: selected.map(s => s.name),
          current_medications: assessment.medications || [],
          current_supplements: assessment.current_supplements || []
        },
        { headers: getAuthHeader() }
      );
      
      setGeneratedProtocol(response.data.schedule);
      setShowProtocol(true);
      toast.success('Protocol generated!');
    } catch (error) {
      console.error('Error generating protocol:', error);
      toast.error('Failed to generate protocol');
    } finally {
      setGeneratingProtocol(false);
    }
  };

  // Download protocol as PDF
  const downloadProtocolPdf = async () => {
    if (!generatedProtocol) return;
    
    setDownloadingProtocolPdf(true);
    try {
      const response = await axios.post(
        `${API}/assessment/${id}/schedule-pdf`,
        { schedule: generatedProtocol },
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `vitala_supplement_protocol_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Protocol downloaded!');
    } catch (error) {
      console.error('Error downloading protocol:', error);
      toast.error('Failed to download protocol');
    } finally {
      setDownloadingProtocolPdf(false);
    }
  };

  // Save protocol to database
  const saveProtocol = async () => {
    if (!generatedProtocol) return;
    
    setSavingProtocol(true);
    try {
      await axios.post(
        `${API}/schedules/save`,
        { 
          assessment_id: id,
          schedule: generatedProtocol
        },
        { headers: getAuthHeader() }
      );
      
      setProtocolSaved(true);
      toast.success('Protocol saved! View it from your Dashboard.');
    } catch (error) {
      console.error('Error saving protocol:', error);
      toast.error('Failed to save protocol');
    } finally {
      setSavingProtocol(false);
    }
  };

  // Get selected count
  const selectedCount = Object.values(selectedSupplements).filter(Boolean).length;

  // Create reminders from protocol schedule
  const createRemindersFromPlan = async () => {
    if (!generatedProtocol) return;
    setCreatingReminders(true);
    try {
      const timeMap = { morning: '08:00', afternoon: '13:00', evening: '18:00', bedtime: '22:00' };
      const items = [];
      for (const [slot, time] of Object.entries(timeMap)) {
        const slotItems = generatedProtocol[slot] || [];
        for (const item of slotItems) {
          items.push({ supplement_name: item.name, time, is_medication: !!item.is_medication });
        }
      }
      if (items.length === 0) {
        toast.info('No supplements found in your protocol to set reminders for');
        return;
      }
      const res = await axios.post(`${API}/reminders/bulk-from-plan`, { items }, { headers: getAuthHeader() });
      setRemindersCreated(true);
      setHasExistingReminders(true);
      if (res.data.skipped > 0) {
        toast.success(`${res.data.created} reminders created (${res.data.skipped} already existed)`);
      } else {
        toast.success(`${res.data.created} reminders created from your plan!`);
      }
    } catch (err) {
      toast.error('Failed to create reminders');
    } finally {
      setCreatingReminders(false);
    }
  };


  // Add a supplement to My Stack
  const addToStack = async (supplement) => {
    setAddingToStack(prev => ({ ...prev, [supplement.name]: true }));
    try {
      await axios.post(`${API}/stack`, {
        name: supplement.name,
        dosage: supplement.dosage || '',
        timing: supplement.timing?.toLowerCase().includes('morning') ? 'morning'
          : supplement.timing?.toLowerCase().includes('evening') ? 'evening'
          : supplement.timing?.toLowerCase().includes('bed') ? 'evening'
          : 'morning',
        category: supplement.category || '',
        supply_days: 30,
        purchase_url_amazon: getAmazonUrl(supplement.name, userCountry),
        purchase_url_iherb: `https://www.iherb.com/search?kw=${encodeURIComponent(supplement.name)}`
      }, { headers: getAuthHeader() });
      setAddedToStack(prev => ({ ...prev, [supplement.name]: true }));
      toast.success(`${supplement.name} added to My Stack!`);
    } catch (err) {
      if (err.response?.data?.message === 'Already in your stack') {
        setAddedToStack(prev => ({ ...prev, [supplement.name]: true }));
        toast.info(`${supplement.name} is already in your stack`);
      } else {
        toast.error('Failed to add to stack');
      }
    } finally {
      setAddingToStack(prev => ({ ...prev, [supplement.name]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Logo size="default" linkTo="/" />
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              data-testid="download-pdf-button"
              className="btn-secondary text-xs sm:text-sm px-2 sm:px-4"
            >
              {downloadingPdf ? (
                <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">{downloadingPdf ? 'Generating...' : 'Share with Provider'}</span>
              <span className="sm:hidden">{downloadingPdf ? '...' : 'Share'}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="btn-secondary text-xs sm:text-sm px-2 sm:px-4"
            >
              <LayoutDashboard className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Dashboard</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Safe-Zone Header - Medical Disclaimer Banner */}
        <div
          className="mb-6 p-4 rounded-xl bg-blue-50 border border-blue-200"
          data-testid="safe-zone-header"
        >
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800">
                <strong>Remember:</strong> These are AI-generated suggestions for <strong>educational purposes only</strong>. 
                Vitala.me is not a medical provider. Always consult with a licensed physician or pharmacist before 
                starting any new supplement regimen.
              </p>
            </div>
          </div>
        </div>

        {/* Share with Healthcare Provider Card */}
        <div
          className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20"
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Share2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-1">
                  Share Your Results
                </h3>
                <p className="text-sm text-muted-foreground">
                  Download PDF, email to your healthcare provider, or create a shareable link.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Download PDF */}
              <Button
                variant="outline"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="btn-secondary"
              >
                {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download PDF
              </Button>
              
              {/* Email to Provider */}
              <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="btn-secondary">
                    <Mail className="w-4 h-4 mr-2" />
                    Email to Provider
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Email Report to Healthcare Provider</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Provider's Email *</Label>
                      <Input
                        type="email"
                        placeholder="doctor@clinic.com"
                        value={emailForm.provider_email}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, provider_email: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Provider's Name (optional)</Label>
                      <Input
                        placeholder="Dr. Smith"
                        value={emailForm.provider_name}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, provider_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Personal Message (optional)</Label>
                      <Textarea
                        placeholder="Hi Dr. Smith, I'd like you to review my supplement recommendations..."
                        value={emailForm.message}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                        rows={3}
                      />
                    </div>
                    <Button
                      onClick={handleEmailToProvider}
                      disabled={sendingEmail}
                      className="w-full btn-primary"
                    >
                      {sendingEmail ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                      ) : (
                        <><Mail className="w-4 h-4 mr-2" />Send Report</>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      The PDF report will be attached to the email automatically.
                    </p>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Create Share Link */}
              <Button
                onClick={handleCreateShareLink}
                disabled={creatingShareLink}
                className="btn-primary"
              >
                {creatingShareLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link className="w-4 h-4 mr-2" />}
                {shareLink ? 'Link Copied!' : 'Get Share Link'}
              </Button>
            </div>
          </div>
          
          {/* Share Link Display */}
          {shareLink && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <Input
                  value={shareLink.share_url}
                  readOnly
                  className="flex-1 bg-muted"
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(shareLink.share_url);
                    toast.success('Link copied!');
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                This link expires on {new Date(shareLink.expires_at).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {/* Plan Comparison Banner (shows after re-assessment) */}
        {planComparison && (planComparison.supplements.added_count > 0 || planComparison.supplements.removed_count > 0) && (() => {
          // Sanity guard: if the AI returned a much shorter recommendation list
          // (removed >0 but added=0 AND no dosage changes), the diff is likely
          // misleading — e.g. a tiny edit (country change) triggered the AI to
          // emit a slimmed-down set. Show a softer, contextual copy instead of
          // alarming the user with a "No Longer Recommended" list.
          const hasNoChange = planComparison.supplements.added_count === 0
            && planComparison.supplements.removed_count > 0
            && planComparison.supplements.kept.filter(s => s.dosage_changed).length === 0;
          if (hasNoChange) {
            return (
              <div
                className="mb-8 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden"
                data-testid="plan-comparison-banner-soft"
              >
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Plan updated</h3>
                  </div>
                  <p className="text-sm text-blue-800 leading-relaxed">
                    Your previous assessment from {new Date(planComparison.previous.created_at).toLocaleDateString()} included {planComparison.supplements.removed_count} additional supplement{planComparison.supplements.removed_count !== 1 ? 's' : ''} that didn&apos;t reappear in this run. They aren&apos;t necessarily wrong for you — your <span className="font-semibold">My Stack</span> still has the authoritative list of what you&apos;re tracking. Re-take a full assessment if you want a comprehensive refresh.
                  </p>
                </div>
              </div>
            );
          }
          return (
          <div
            className="mb-8 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden"
            data-testid="plan-comparison-banner"
          >
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <RefreshCw className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-blue-900">What Changed in Your Plan</h3>
              </div>
              <p className="text-xs text-blue-700 mb-4">
                Compared to your assessment from {new Date(planComparison.previous.created_at).toLocaleDateString()}
              </p>

              {planComparison.supplements.added.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-emerald-700 mb-1.5 flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> New Additions
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {planComparison.supplements.added.map(s => (
                      <span key={s.name} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium border border-emerald-200">
                        {s.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {planComparison.supplements.removed.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-red-700 mb-1.5 flex items-center gap-1">
                    <Minus className="w-3.5 h-3.5" /> No Longer Recommended
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {planComparison.supplements.removed.map(s => (
                      <span key={s.name} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium border border-red-200 line-through">
                        {s.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {planComparison.supplements.kept.filter(s => s.dosage_changed).length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1.5 flex items-center gap-1">
                    <RefreshCw className="w-3.5 h-3.5" /> Dosage Adjusted
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {planComparison.supplements.kept.filter(s => s.dosage_changed).map(s => (
                      <span key={s.name} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium border border-amber-200">
                        {s.name}: <span className="line-through opacity-60">{s.prev_dosage}</span> {s.dosage}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Score changes summary */}
              {Object.keys(planComparison.changes).length > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-200/50">
                  <p className="text-xs font-semibold text-blue-800 mb-2">Health Score Changes</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(planComparison.changes)
                      .filter(([, v]) => v.change !== 0)
                      .sort(([, a], [, b]) => Math.abs(b.change) - Math.abs(a.change))
                      .slice(0, 6)
                      .map(([cat, v]) => (
                        <span
                          key={cat}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            v.improved ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {v.improved ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {cat.replace('_', ' ')} {v.change > 0 ? '+' : ''}{v.change}%
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {/* Quick Actions */}
        <div
          className="mb-8 flex flex-wrap gap-3"
        >
          <ShareResultsButton assessmentId={id} getAuthHeader={getAuthHeader} />
          <Button
            variant="outline"
            onClick={() => navigate('/my-stack')}
            className="btn-secondary"
            data-testid="view-mystack-btn"
          >
            <Package className="w-4 h-4 mr-2" />
            My Stack
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/reminders')}
            className="btn-secondary"
          >
            <Bell className="w-4 h-4 mr-2" />
            {hasExistingReminders || remindersCreated ? 'See Your Reminders' : 'Set Reminders'}
          </Button>
        </div>

        {/* Results Header */}
        <div
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/logo192.png" alt="Vitala" className="w-8 h-8 object-contain" />
            <span className="text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: 'Montserrat, sans-serif' }}>Vitala</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-2">
            Personalized Assessment Results
          </h1>
          <p className="text-xl text-primary font-medium mb-4">
            Prepared for: {user?.name || 'You'}
          </p>
        </div>

        {/* Section 1: Health Assessment Scores - Compact Version */}
        <div
          className="mb-8"
        >
          <h2 className="text-xl font-bold text-[#1A1A1A] text-center mb-4">
            Health Assessment Scores
          </h2>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {sortedScores.map(([category, score]) => {
              const Icon = categoryIcons[category] || Shield;
              const color = categoryColors[category] || 'bg-gray-100 text-gray-600 border-gray-200';
              return (
                <div 
                  key={category}
                  className="bg-white rounded-lg border border-border/50 p-2 shadow-sm"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className={`w-5 h-5 rounded ${color.split(' ')[0]} flex items-center justify-center`}>
                      <Icon className="w-3 h-3" />
                    </div>
                    <span className="font-medium capitalize text-xs truncate">{category.replace('_', ' ')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-lg font-bold text-[#1A1A1A]">{score}%</span>
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${score}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* BMI Card - Now directly under Health Scores */}
          {assessment.bmi && (
            <div className="mt-4 bg-white rounded-xl border border-border/50 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    assessment.bmi_category === 'Normal' ? 'bg-green-100' :
                    assessment.bmi_category === 'Underweight' ? 'bg-blue-100' :
                    assessment.bmi_category === 'Overweight' ? 'bg-amber-100' : 'bg-red-100'
                  }`}>
                    <Scale className={`w-4 h-4 ${
                      assessment.bmi_category === 'Normal' ? 'text-green-600' :
                      assessment.bmi_category === 'Underweight' ? 'text-blue-600' :
                      assessment.bmi_category === 'Overweight' ? 'text-amber-600' : 'text-red-600'
                    }`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">BMI</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-[#1A1A1A]">{assessment.bmi}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        assessment.bmi_category === 'Normal' ? 'bg-green-100 text-green-700' :
                        assessment.bmi_category === 'Underweight' ? 'bg-blue-100 text-blue-700' :
                        assessment.bmi_category === 'Overweight' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {assessment.bmi_category}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 text-xs">
                  {assessment.weight_kg && (
                    <div>
                      <p className="text-muted-foreground">Weight</p>
                      <p className="font-semibold">{assessment.weight_kg} kg</p>
                    </div>
                  )}
                  {assessment.height_cm && (
                    <div>
                      <p className="text-muted-foreground">Height</p>
                      <p className="font-semibold">{assessment.height_cm} cm</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Current Medications/Supplements */}
        {(assessment.medications?.length > 0 || assessment.current_supplements?.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <h2 className="text-xl font-bold text-[#1A1A1A] text-center mb-4">
              Current Medications & Supplements
            </h2>
            <div className="bg-white rounded-2xl border border-border/50 p-4 shadow-sm">
              <div className="flex flex-wrap gap-2">
                {assessment.medications?.map((med, i) => (
                  <span 
                    key={`med-${i}`}
                    className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium border border-blue-200"
                  >
                    <Pill className="w-3 h-3 inline mr-1" />
                    {med}
                  </span>
                ))}
                {assessment.current_supplements?.map((supp, i) => (
                  <span 
                    key={`supp-${i}`}
                    className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200"
                  >
                    {supp}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Section 3: Your Current Protocol - Auto-displayed */}
        {generatedProtocol && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <h2 className="text-xl font-bold text-[#1A1A1A] text-center mb-2">
              Your Current Protocol
            </h2>
            <p className="text-muted-foreground text-center text-sm mb-4">
              Optimized timing to maximize absorption and avoid interactions
            </p>
            
            <div className="bg-white rounded-2xl border border-border/50 p-6 shadow-sm">
              {/* Time Slots */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Morning */}
                {generatedProtocol.morning && generatedProtocol.morning.length > 0 && (
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Sun className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-900">Morning</span>
                    </div>
                    <div className="space-y-2">
                      {generatedProtocol.morning.map((item, i) => (
                        <div key={i} className={`flex items-start gap-2 text-sm ${item.is_medication ? 'text-blue-700' : 'text-amber-800'}`}>
                          <span className={`mt-0.5 text-xs px-1.5 py-0.5 rounded ${item.is_medication ? 'bg-blue-100' : 'bg-amber-100'}`}>
                            {item.is_medication ? 'Rx' : '•'}
                          </span>
                          <div>
                            <span className="font-medium">{item.name}</span>
                            {item.dosage && <span className="text-xs ml-1">({item.dosage})</span>}
                            {item.note && <p className="text-xs opacity-75">{item.note}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Afternoon */}
                {generatedProtocol.afternoon && generatedProtocol.afternoon.length > 0 && (
                  <div className="bg-sky-50 rounded-xl p-4 border border-sky-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Coffee className="w-5 h-5 text-sky-600" />
                      <span className="font-semibold text-sky-900">Afternoon</span>
                    </div>
                    <div className="space-y-2">
                      {generatedProtocol.afternoon.map((item, i) => (
                        <div key={i} className={`flex items-start gap-2 text-sm ${item.is_medication ? 'text-blue-700' : 'text-sky-800'}`}>
                          <span className={`mt-0.5 text-xs px-1.5 py-0.5 rounded ${item.is_medication ? 'bg-blue-100' : 'bg-sky-100'}`}>
                            {item.is_medication ? 'Rx' : '•'}
                          </span>
                          <div>
                            <span className="font-medium">{item.name}</span>
                            {item.dosage && <span className="text-xs ml-1">({item.dosage})</span>}
                            {item.note && <p className="text-xs opacity-75">{item.note}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Evening */}
                {generatedProtocol.evening && generatedProtocol.evening.length > 0 && (
                  <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Sunset className="w-5 h-5 text-orange-600" />
                      <span className="font-semibold text-orange-900">Evening</span>
                    </div>
                    <div className="space-y-2">
                      {generatedProtocol.evening.map((item, i) => (
                        <div key={i} className={`flex items-start gap-2 text-sm ${item.is_medication ? 'text-blue-700' : 'text-orange-800'}`}>
                          <span className={`mt-0.5 text-xs px-1.5 py-0.5 rounded ${item.is_medication ? 'bg-blue-100' : 'bg-orange-100'}`}>
                            {item.is_medication ? 'Rx' : '•'}
                          </span>
                          <div>
                            <span className="font-medium">{item.name}</span>
                            {item.dosage && <span className="text-xs ml-1">({item.dosage})</span>}
                            {item.note && <p className="text-xs opacity-75">{item.note}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Bedtime */}
                {generatedProtocol.bedtime && generatedProtocol.bedtime.length > 0 && (
                  <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Moon className="w-5 h-5 text-indigo-600" />
                      <span className="font-semibold text-indigo-900">Bedtime</span>
                    </div>
                    <div className="space-y-2">
                      {generatedProtocol.bedtime.map((item, i) => (
                        <div key={i} className={`flex items-start gap-2 text-sm ${item.is_medication ? 'text-blue-700' : 'text-indigo-800'}`}>
                          <span className={`mt-0.5 text-xs px-1.5 py-0.5 rounded ${item.is_medication ? 'bg-blue-100' : 'bg-indigo-100'}`}>
                            {item.is_medication ? 'Rx' : '•'}
                          </span>
                          <div>
                            <span className="font-medium">{item.name}</span>
                            {item.dosage && <span className="text-xs ml-1">({item.dosage})</span>}
                            {item.note && <p className="text-xs opacity-75">{item.note}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Important Alerts */}
              {generatedProtocol.alerts && generatedProtocol.alerts.length > 0 && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-900 text-sm mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Important Timing Notes
                  </h4>
                  <ul className="space-y-1">
                    {generatedProtocol.alerts.map((alert, i) => (
                      <li key={i} className="text-xs text-amber-800">• {alert}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={downloadProtocolPdf} disabled={downloadingProtocolPdf}>
                  {downloadingProtocolPdf ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                  Download PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="w-3 h-3 mr-1" />
                  Print
                </Button>
                <Button variant="outline" size="sm" onClick={saveProtocol} disabled={savingProtocol || protocolSaved}>
                  {savingProtocol ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : protocolSaved ? <BookmarkCheck className="w-3 h-3 mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                  {protocolSaved ? 'Saved!' : 'Save Protocol'}
                </Button>
                <Button
                  size="sm"
                  onClick={async () => {
                    if (remindersCreated) {
                      navigate('/reminders');
                      return;
                    }
                    await createRemindersFromPlan();
                    setTimeout(() => navigate('/reminders'), 800);
                  }}
                  disabled={creatingReminders}
                  className="btn-primary"
                  data-testid="set-reminders-btn"
                >
                  {creatingReminders ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Bell className="w-3 h-3 mr-1" />}
                  {creatingReminders ? 'Setting...' : remindersCreated ? 'Reminders are Set' : 'Set Reminders'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Section 4: Personalized Health Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-8"
        >
          <h2 className="text-xl font-bold text-[#1A1A1A] text-center mb-4">
            Personalized Health Insights
          </h2>
          <div className="bg-white rounded-2xl border border-border/50 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Brain className="w-4 h-4 text-primary" />
              </div>
              <span className="font-semibold text-[#1A1A1A]">AI Analysis</span>
            </div>
            <div className="prose prose-sm max-w-none text-[#1A1A1A]/80 leading-relaxed">
              {(() => {
                const { healthInsights } = parseAIRecommendation(assessment.ai_recommendation);
                return healthInsights.split('\n').map((line, i) => {
                  const trimmed = line.trim();
                  if (!trimmed) return null;
                  
                  // Handle bullet points
                  if (trimmed.startsWith('•') || trimmed.startsWith('-')) {
                    const bulletText = trimmed.replace(/^[•-]\s*/, '');
                    const parts = bulletText.split(/\*\*(.+?)\*\*/g);
                    return (
                      <div key={i} className="flex items-start gap-2 mb-2 ml-2">
                        <span className="text-primary mt-1">•</span>
                        <span>
                          {parts.map((part, j) => 
                            j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                          )}
                        </span>
                      </div>
                    );
                  }
                  
                  return <p key={i} className="mb-3 last:mb-0">{trimmed}</p>;
                });
              })()}
            </div>
          </div>
        </motion.div>

        {/* Section 5: Lifestyle Tips */}
        {(() => {
          const { lifestyleTips } = parseAIRecommendation(assessment.ai_recommendation);
          if (!lifestyleTips) return null;
          
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-8"
            >
              <h2 className="text-xl font-bold text-[#1A1A1A] text-center mb-4">
                Lifestyle Tips
              </h2>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="font-semibold text-emerald-900">Recommendations for You</span>
                </div>
                <div className="prose prose-sm max-w-none text-emerald-800 leading-relaxed">
                  {lifestyleTips.split('\n').map((line, i) => {
                    const trimmed = line.trim();
                    if (!trimmed) return null;
                    
                    if (trimmed.startsWith('•') || trimmed.startsWith('-')) {
                      const bulletText = trimmed.replace(/^[•-]\s*/, '');
                      const parts = bulletText.split(/\*\*(.+?)\*\*/g);
                      return (
                        <div key={i} className="flex items-start gap-2 mb-2">
                          <span className="text-emerald-500 mt-1">•</span>
                          <span>
                            {parts.map((part, j) => 
                              j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                            )}
                          </span>
                        </div>
                      );
                    }
                    
                    return <p key={i} className="mb-3 last:mb-0">{trimmed}</p>;
                  })}
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* Section 6: Supplement Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-[#1A1A1A] text-center mb-2">
            Supplement Recommendations Based on Your Results
          </h2>
          <p className="text-amber-600 font-semibold text-center mb-6 bg-amber-50 py-2 px-4 rounded-lg border border-amber-200">
            Select the supplements you'd like to add to your protocol
          </p>

          {/* Selection Controls & Generate Button */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 p-4 bg-primary/5 rounded-xl border border-primary/20">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllSupplements}
                  className="text-xs"
                >
                  <CheckSquare className="w-3 h-3 mr-1" />
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllSelections}
                  className="text-xs"
                >
                  <Square className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              </div>
              <span className="text-sm text-muted-foreground">
                {selectedCount} of {sortedSupplements.length} selected
              </span>
            </div>
            
            <Button
              onClick={generateProtocol}
              disabled={selectedCount === 0 || generatingProtocol}
              className={`${selectedCount > 0 ? 'btn-primary animate-pulse' : 'btn-primary'}`}
              data-testid="generate-protocol-btn"
            >
              {generatingProtocol ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ListChecks className="w-4 h-4 mr-2" />
              )}
              {generatingProtocol 
                ? 'Generating...' 
                : selectedCount > 0 
                  ? 'Generate your new protocol' 
                  : 'Make your selection to generate new protocol'}
            </Button>
          </div>

          <div className="space-y-4">
            {regularSupplements.map((supplement, index) => {
              const Icon = categoryIcons[supplement.category] || Shield;
              const color = categoryColors[supplement.category] || 'bg-gray-100 text-gray-600 border-gray-200';
              const TimingIcon = getTimingIcon(supplement.timing);
              const hasWarnings = supplement.warnings && supplement.warnings.length > 0;
              const isSelected = selectedSupplements[supplement.name];
              const isContinueToTake = supplement.priority === 'current';
              
              return (
                <motion.div
                  key={index}
                  data-testid={`supplement-card-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.05 }}
                  className={`bg-white rounded-2xl border overflow-hidden hover:shadow-[0_8px_24px_rgba(74,108,88,0.08)] transition-all ${
                    isSelected ? 'border-primary border-2 ring-2 ring-primary/20' : 'border-border/50'
                  } ${isContinueToTake ? 'bg-emerald-50/30' : ''}`}
                >
                  <Accordion type="single" collapsible>
                    <AccordionItem value={`item-${index}`} className="border-0">
                      <div className="flex items-center">
                        {/* Checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSupplementSelection(supplement.name);
                          }}
                          className={`ml-4 w-6 h-6 min-w-[24px] min-h-[24px] rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                            isSelected 
                              ? 'bg-primary border-primary text-white' 
                              : 'bg-white border-gray-300 hover:border-primary'
                          }`}
                          data-testid={`supplement-checkbox-${index}`}
                        >
                          {isSelected && <Check className="w-4 h-4" />}
                        </button>
                        
                        <AccordionTrigger className="flex-1 px-4 py-5 hover:no-underline">
                          <div className="flex items-center gap-4 w-full">
                            {/* Priority & Category */}
                            <div className="flex flex-col items-center gap-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${priorityColors[supplement.priority]}`}>
                                {supplement.priority === 'current' ? 'Continue to Take' : supplement.priority}
                              </span>
                              <div className={`w-10 h-10 rounded-full ${color.split(' ')[0]} flex items-center justify-center`}>
                                <Icon className="w-5 h-5" />
                              </div>
                            </div>
                            
                            {/* Main Info */}
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2">
                                <h3 className="text-lg font-semibold text-[#1A1A1A]">{supplement.name}</h3>
                                {hasWarnings && (
                                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1 font-medium text-primary">
                                  <Pill className="w-4 h-4" />
                                  {supplement.dosage}
                                </span>
                                <span className="flex items-center gap-1">
                                  <TimingIcon className="w-4 h-4" />
                                  {supplement.timing}
                                </span>
                              </div>
                            </div>
                          </div>
                        </AccordionTrigger>
                      </div>
                      
                      <AccordionContent className="px-6 pb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
                          {/* Left Column */}
                          <div className="space-y-4">
                            {/* Why This Supplement - Based on your assessment */}
                            {supplement.reason && (
                              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                  <Lightbulb className="w-4 h-4 text-primary" />
                                  Why This Supplement?
                                </h4>
                                <p className="text-sm text-muted-foreground">{supplement.reason}</p>
                              </div>
                            )}
                            
                            {/* How to Take */}
                            <div>
                              <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                <Info className="w-4 h-4 text-primary" />
                                How to Take
                              </h4>
                              <p className="text-sm text-muted-foreground">{supplement.how_to_take}</p>
                            </div>
                            
                            {/* Duration */}
                            <div>
                              <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-primary" />
                                Duration
                              </h4>
                              <p className="text-sm text-muted-foreground">{supplement.duration}</p>
                            </div>
                            
                            {/* Warnings */}
                            {hasWarnings && (
                              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                                <h4 className="text-sm font-medium text-amber-900 mb-1 flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4" />
                                  Important Note
                                </h4>
                                {supplement.warnings.map((warning, wi) => (
                                  <p key={wi} className="text-sm text-amber-800">{warning}</p>
                                ))}
                              </div>
                            )}
                            
                            {/* Contraindications - Who Should NOT Take */}
                            {supplement.contraindications && supplement.contraindications.length > 0 && (
                              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                                <h4 className="text-sm font-medium text-red-900 mb-2 flex items-center gap-2">
                                  <XCircle className="w-4 h-4" />
                                  Who Should Avoid
                                </h4>
                                <ul className="space-y-1">
                                  {supplement.contraindications.map((contra, ci) => (
                                    <li key={ci} className="text-sm text-red-800 flex items-start gap-2">
                                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                                      {contra}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          
                          {/* Right Column */}
                          <div className="space-y-4">
                            {/* Benefits */}
                            <div>
                              <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                                Key Benefits
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {(Array.isArray(supplement.benefits) 
                                  ? supplement.benefits 
                                  : (supplement.benefits || '').split(';').map(b => b.trim()).filter(b => b)
                                ).map((benefit, bi) => (
                                  <span key={bi} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">
                                    {benefit}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            {/* What to Look For */}
                            <div>
                              <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-primary" />
                                What to Look For
                              </h4>
                              <p className="text-sm text-muted-foreground">{supplement.what_to_look_for}</p>
                            </div>
                            
                            {/* Research Evidence Section */}
                            <div className="pt-4 border-t border-border">
                              <button
                                onClick={() => fetchResearch(supplement.name)}
                                className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                                data-testid={`view-research-${supplement.name.replace(/\s/g, '-').toLowerCase()}`}
                              >
                                {loadingResearch[supplement.name] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <BookOpen className="w-4 h-4" />
                                )}
                                {expandedResearch[supplement.name] ? 'Hide Research' : 'View Research'}
                              </button>
                              
                              {expandedResearch[supplement.name] && supplementResearch[supplement.name] && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="mt-3 p-4 bg-blue-50 rounded-lg border border-blue-200"
                                >
                                  <div className="flex items-start gap-2 mb-3">
                                    <FlaskConical className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <h5 className="font-semibold text-blue-900 text-sm">Research Evidence</h5>
                                      <p className="text-blue-800 text-sm mt-1">
                                        "{supplementResearch[supplement.name].key_finding}"
                                      </p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex flex-wrap gap-2 mt-3 text-xs">
                                    <span className="px-2 py-1 bg-white rounded border border-blue-200 text-blue-700">
                                      {supplementResearch[supplement.name].study_type}
                                    </span>
                                    <span className="px-2 py-1 bg-white rounded border border-blue-200 text-blue-700">
                                      {supplementResearch[supplement.name].year}
                                    </span>
                                    {supplementResearch[supplement.name].sample_size && (
                                      <span className="px-2 py-1 bg-white rounded border border-blue-200 text-blue-700">
                                        {supplementResearch[supplement.name].sample_size}
                                      </span>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-blue-200">
                                    <div className="flex items-center gap-2">
                                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getEvidenceColor(supplementResearch[supplement.name].evidence_strength)}`}>
                                        {getEvidenceDots(supplementResearch[supplement.name].evidence_strength)} {supplementResearch[supplement.name].evidence_strength?.charAt(0).toUpperCase() + supplementResearch[supplement.name].evidence_strength?.slice(1)}
                                      </span>
                                    </div>
                                    
                                    {supplementResearch[supplement.name].pubmed_url && (
                                      <a
                                        href={supplementResearch[supplement.name].pubmed_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                      >
                                        <GraduationCap className="w-3.5 h-3.5" />
                                        {supplementResearch[supplement.name].pubmed_id?.startsWith('PMC') 
                                          ? `PubMed: ${supplementResearch[supplement.name].pubmed_id}`
                                          : `View Study`
                                        }
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                  </div>
                                  
                                  <p className="text-xs text-blue-600 mt-2 italic">
                                    Source: {supplementResearch[supplement.name].source}
                                  </p>
                                </motion.div>
                              )}
                              
                              {expandedResearch[supplement.name] && supplementResearch[supplement.name] === null && (
                                <p className="mt-2 text-xs text-muted-foreground italic">
                                  Research data not yet available for this supplement.
                                </p>
                              )}
                            </div>
                            
                            {/* Where to Buy - Affiliate Link */}
                            {affiliateSettings?.enabled && dynamicRetailers.length > 0 && (
                              <div className="pt-4 border-t border-border">
                                <h4 className="text-sm font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                                  <ShoppingCart className="w-4 h-4 text-primary" />
                                  Where to Buy
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {dynamicRetailers.map((retailer) => {
                                    const url = retailer.search_url_template.replace(
                                      '{query}',
                                      encodeURIComponent(supplement.name)
                                    );
                                    return (
                                      <a
                                        key={retailer.id}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => {
                                          axios.post(`${API}/affiliate/click/search`).catch(() => {});
                                        }}
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${retailer.color_class}`}
                                        data-testid={`buy-${retailer.id}-${supplement.name.replace(/\s/g, '-').toLowerCase()}`}
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                        {retailer.name}
                                      </a>
                                    );
                                  })}
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 italic">
                                  We do not earn commissions from these links. They are provided for your convenience.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Add to My Stack */}
                        {!supplement.isCurrentSupplement && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <button
                              onClick={(e) => { e.stopPropagation(); addToStack(supplement); }}
                              disabled={addingToStack[supplement.name] || addedToStack[supplement.name]}
                              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                addedToStack[supplement.name]
                                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                  : 'bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20'
                              }`}
                              data-testid={`add-to-stack-${supplement.name.replace(/\s/g, '-').toLowerCase()}`}
                            >
                              {addingToStack[supplement.name] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : addedToStack[supplement.name] ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                              {addedToStack[supplement.name] ? 'Added to Stack' : 'Add to My Stack'}
                            </button>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </motion.div>
              );
            })}
          </div>

          {/* Quit Support Section */}
          {cessationSupplements.length > 0 && (
            <div className="mt-8" data-testid="quit-support-section">
              <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5 mb-4">
                <h3 className="text-lg font-bold text-teal-900 flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-teal-600" />
                  Quit Support Protocol
                </h3>
                <p className="text-sm text-teal-800">
                  Based on your responses, these supplements are specifically recommended to support your cessation goals. 
                  They work best alongside a comprehensive quit plan — talk to your healthcare provider about combining supplements with medical cessation programs.
                </p>
              </div>

              <div className="space-y-4">
                {cessationSupplements.map((supplement, index) => {
                  const Icon = categoryIcons[supplement.category] || Shield;
                  const color = categoryColors[supplement.category] || 'bg-gray-100 text-gray-600 border-gray-200';
                  const TimingIcon = getTimingIcon(supplement.timing);
                  const hasWarnings = supplement.warnings && supplement.warnings.length > 0;
                  const isSelected = selectedSupplements[supplement.name];

                  return (
                    <motion.div
                      key={`cessation-${supplement.name}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 * index }}
                      className={`bg-white rounded-xl border-2 transition-all ${
                        isSelected ? 'border-teal-400 shadow-md' : 'border-border hover:border-teal-300'
                      }`}
                    >
                      <Accordion type="single" collapsible>
                        <AccordionItem value={`cessation-${supplement.name}`} className="border-none">
                          <div className="p-4">
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => toggleSupplementSelection(supplement.name)}
                                className="mt-0.5 flex-shrink-0"
                                data-testid={`select-cessation-${supplement.name.replace(/\s/g, '-').toLowerCase()}`}
                              >
                                {isSelected ? (
                                  <CheckSquare className="w-5 h-5 text-teal-600" />
                                ) : (
                                  <Square className="w-5 h-5 text-muted-foreground hover:text-teal-500" />
                                )}
                              </button>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${color}`}>
                                    <Icon className="w-3 h-3" />
                                    {supplement.category === 'smoking_cessation' ? 'Quit Smoking' : 'Reduce Alcohol'}
                                  </span>
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-700 border border-teal-200">
                                    Quit Support
                                  </span>
                                </div>
                                
                                <AccordionTrigger className="p-0 hover:no-underline [&>svg]:hidden">
                                  <h3 className="text-base font-semibold text-[#1A1A1A] text-left">{supplement.name}</h3>
                                </AccordionTrigger>
                                
                                <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1"><Pill className="w-3 h-3" /> {supplement.dosage}</span>
                                  <span className="flex items-center gap-1"><TimingIcon className="w-3 h-3" /> {supplement.timing}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <AccordionContent className="px-4 pb-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                              <div className="space-y-4">
                                {supplement.reason && (
                                  <div className="p-3 rounded-lg bg-teal-50 border border-teal-200">
                                    <h4 className="text-sm font-medium text-teal-900 mb-2 flex items-center gap-2">
                                      <Lightbulb className="w-4 h-4 text-teal-600" />
                                      Why This Supplement?
                                    </h4>
                                    <p className="text-sm text-teal-800">{supplement.reason}</p>
                                  </div>
                                )}
                                <div>
                                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                    <Info className="w-4 h-4 text-teal-600" />
                                    How to Take
                                  </h4>
                                  <p className="text-sm text-muted-foreground">{supplement.how_to_take}</p>
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-teal-600" />
                                    Duration
                                  </h4>
                                  <p className="text-sm text-muted-foreground">{supplement.duration}</p>
                                </div>
                                {hasWarnings && (
                                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                                    <h4 className="text-sm font-medium text-amber-900 mb-1 flex items-center gap-2">
                                      <AlertTriangle className="w-4 h-4" />
                                      Important Note
                                    </h4>
                                    {supplement.warnings.map((warning, wi) => (
                                      <p key={wi} className="text-sm text-amber-800">{warning}</p>
                                    ))}
                                  </div>
                                )}
                                {supplement.contraindications && supplement.contraindications.length > 0 && (
                                  <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                                    <h4 className="text-sm font-medium text-red-900 mb-2 flex items-center gap-2">
                                      <XCircle className="w-4 h-4" />
                                      Who Should Avoid
                                    </h4>
                                    <ul className="space-y-1">
                                      {supplement.contraindications.map((contra, ci) => (
                                        <li key={ci} className="text-sm text-red-800 flex items-start gap-2">
                                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                                          {contra}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                    Key Benefits
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {(Array.isArray(supplement.benefits) 
                                      ? supplement.benefits 
                                      : [supplement.benefits]
                                    ).filter(Boolean).map((benefit, bi) => (
                                      <span key={bi} className="px-3 py-1.5 bg-teal-50 text-teal-700 text-xs rounded-lg border border-teal-200">
                                        {benefit}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                {supplement.what_to_look_for && (
                                  <div>
                                    <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                      <Eye className="w-4 h-4 text-teal-600" />
                                      What to Look For
                                    </h4>
                                    <p className="text-sm text-muted-foreground">{supplement.what_to_look_for}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>

        {/* Duplicate CTA Button - Above Important Timing Notes */}
        {selectedCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.52 }}
            className="mt-6 mb-4"
          >
            <div className="flex justify-center">
              <Button
                onClick={generateProtocol}
                disabled={selectedCount === 0 || generatingProtocol}
                className="btn-primary animate-pulse px-8 py-3 text-base"
                data-testid="generate-protocol-btn-bottom"
              >
                {generatingProtocol ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <ListChecks className="w-5 h-5 mr-2" />
                )}
                {generatingProtocol 
                  ? 'Generating...' 
                  : `Generate your new protocol (${selectedCount} selected)`}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Important Timing Notes - Only for supplements user is actually taking */}
        {(() => {
          // Get supplements user is currently taking
          const currentSupps = assessment.current_supplements || [];
          const currentSuppsLower = currentSupps.map(s => s.toLowerCase());
          
          // Filter interactions to only show for supplements user is actually taking
          // For supplement-supplement interactions (e.g., "Zinc + Multivitamin"), 
          // BOTH supplements must be in user's current list
          const relevantInteractions = (assessment.drug_interactions || []).filter(inter => {
            const suppName = (inter.supplement || '').toLowerCase();
            
            // Check if this is a supplement-supplement interaction (contains " + ")
            if (suppName.includes(' + ')) {
              const parts = suppName.split(' + ').map(p => p.trim().toLowerCase());
              // Both supplements must be in user's current supplements
              return parts.every(part => 
                currentSuppsLower.some(cs => 
                  cs.includes(part) || part.includes(cs) ||
                  cs.replace(/\s+/g, '').includes(part.replace(/\s+/g, '')) ||
                  part.replace(/\s+/g, '').includes(cs.replace(/\s+/g, ''))
                )
              );
            }
            
            // For single-supplement interactions, check normally
            return currentSuppsLower.some(cs => 
              cs.includes(suppName) || suppName.includes(cs) ||
              cs.replace(/\s+/g, '').includes(suppName.replace(/\s+/g, '')) ||
              suppName.replace(/\s+/g, '').includes(cs.replace(/\s+/g, ''))
            );
          });
          
          if (relevantInteractions.length === 0) return null;
          
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="mt-8 mb-8"
            >
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Important Timing Notes
                </h3>
                <ul className="space-y-2">
                  {relevantInteractions.map((interaction, i) => (
                    interaction.timing_recommendation && (
                      <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                        <span className="mt-1.5">•</span>
                        <span>
                          <strong>{interaction.supplement}:</strong> {interaction.timing_recommendation}
                        </span>
                      </li>
                    )
                  ))}
                </ul>
              </div>
            </motion.div>
          );
        })()}

        {/* Section 7: Medication-Supplement Interactions */}
        {/* Only show interactions for supplements user is taking OR has selected */}
        {(() => {
          // Get list of supplements user is actually taking or has selected
          const userSupplements = [
            ...(assessment.current_supplements || []),
            ...Object.keys(selectedSupplements).filter(name => selectedSupplements[name])
          ].map(s => s.toLowerCase());
          
          // Filter interactions to only show relevant ones
          // For supplement-supplement interactions (e.g., "Zinc + Multivitamin"), 
          // BOTH supplements must be in user's list
          const relevantInteractions = (assessment.drug_interactions || []).filter(interaction => {
            const suppName = (interaction.supplement || '').toLowerCase();
            
            // Check if this is a supplement-supplement interaction (contains " + ")
            if (suppName.includes(' + ')) {
              const parts = suppName.split(' + ').map(p => p.trim().toLowerCase());
              // Both supplements must be in user's supplements
              return parts.every(part => 
                userSupplements.some(userSupp => 
                  userSupp.includes(part) || part.includes(userSupp) ||
                  (part.includes('omega') && userSupp.includes('omega')) ||
                  (part.includes('fish') && userSupp.includes('fish'))
                )
              );
            }
            
            // For single-supplement or medication interactions, check normally
            return userSupplements.some(userSupp => 
              suppName.includes(userSupp) || userSupp.includes(suppName) ||
              // Handle partial matches like "fish oil" matching "omega-3"
              (suppName.includes('omega') && userSupp.includes('omega')) ||
              (suppName.includes('fish') && userSupp.includes('fish'))
            );
          });
          
          if (relevantInteractions.length === 0) return null;
          
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-8 mb-8"
            >
              <h2 className="text-xl font-bold text-[#1A1A1A] text-center mb-4">
                Medication-Supplement Interactions
              </h2>
              
              {/* Warning Banner */}
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900 text-sm">Important Safety Information</h3>
                    <p className="text-amber-800 text-xs">
                      Based on your medications and supplements, please review these potential interactions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Severity summary chips — 3-second overview */}
              {(() => {
                const counts = { avoid: 0, caution: 0, monitor: 0 };
                relevantInteractions.forEach(it => {
                  const t = (it.interaction_type || '').toLowerCase();
                  if (counts[t] !== undefined) counts[t] += 1;
                });
                const chips = [
                  { key: 'avoid', label: 'to avoid', count: counts.avoid, classes: 'bg-red-50 border-red-300 text-red-700' },
                  { key: 'caution', label: 'to time carefully', count: counts.caution, classes: 'bg-amber-50 border-amber-300 text-amber-800' },
                  { key: 'monitor', label: 'to monitor', count: counts.monitor, classes: 'bg-blue-50 border-blue-300 text-blue-700' },
                ];
                return (
                  <div className="grid grid-cols-3 gap-2 mb-4" data-testid="interaction-severity-summary">
                    {chips.map(c => (
                      <div
                        key={c.key}
                        className={`rounded-lg border px-2 py-2 text-center ${c.classes}`}
                        data-testid={`severity-chip-${c.key}`}
                      >
                        <p className="text-lg font-bold leading-none">{c.count}</p>
                        <p className="text-[10px] leading-tight mt-1">{c.label}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Card Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {relevantInteractions.map((interaction, i) => {
                  const isAvoid = interaction.interaction_type?.toLowerCase() === 'avoid';
                  const isCaution = interaction.interaction_type?.toLowerCase() === 'caution';
                  const isMonitor = interaction.interaction_type?.toLowerCase() === 'monitor';

                  const borderColor = isAvoid ? 'border-red-400' : isCaution ? 'border-amber-400' : isMonitor ? 'border-blue-400' : 'border-emerald-400';
                  const bgColor = isAvoid ? 'bg-red-50' : isCaution ? 'bg-amber-50' : isMonitor ? 'bg-blue-50' : 'bg-emerald-50';
                  const badgeBg = isAvoid ? 'bg-red-500' : isCaution ? 'bg-amber-500' : isMonitor ? 'bg-blue-500' : 'bg-emerald-500';
                  const iconColor = isAvoid ? 'text-red-500' : isCaution ? 'text-amber-500' : isMonitor ? 'text-blue-500' : 'text-emerald-500';
                  
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * i }}
                      className={`${bgColor} ${borderColor} border-l-4 rounded-lg p-3 shadow-sm`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <span className={`inline-block ${badgeBg} text-white text-xs font-bold uppercase px-2 py-0.5 rounded mb-1`}>
                            {interaction.interaction_type || 'Monitor'}
                          </span>
                          <h4 className="font-semibold text-[#1A1A1A] text-sm leading-tight">
                            {interaction.medication}
                            <span className="text-muted-foreground font-normal"> + </span>
                            {interaction.supplement}
                          </h4>
                        </div>
                        <div className={`${iconColor} ml-2`}>
                          {isAvoid ? <XCircle className="w-5 h-5" /> : isCaution ? <AlertTriangle className="w-5 h-5" /> : <Info className="w-5 h-5" />}
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                        {interaction.description}
                      </p>
                      
                      {interaction.timing_recommendation && (
                        <div className="flex items-start gap-1.5 bg-white/70 rounded p-1.5 border border-gray-100">
                          <Clock className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                          <p className="text-xs">
                            <span className="font-medium text-primary">Timing: </span>
                            <span className="text-[#1A1A1A]">{interaction.timing_recommendation}</span>
                          </p>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          );
        })()}

        {/* Section 8: Revise Answers CTA (Last Section) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="mt-8 text-center p-8 rounded-2xl bg-white border border-border/50"
        >
          <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
            Want to update your profile?
          </h3>
          <p className="text-muted-foreground text-sm mb-4">
            Revise your answers to get updated recommendations. Your previous responses are pre-filled — just change what's different.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={() => navigate('/edit-answers')}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="edit-answers-cta"
            >
              Edit specific answers
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/quiz?prefill=true')}
              className="btn-primary"
            >
              Retake full assessment
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      </main>

      {/* Protocol Modal */}
      {showProtocol && generatedProtocol && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowProtocol(false);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-border flex items-center justify-between bg-gradient-to-r from-primary/10 to-primary/5">
              <div>
                <h2 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-primary" />
                  Your Supplement Protocol
                </h2>
                <p className="text-muted-foreground mt-1">Optimized timing to maximize absorption and avoid interactions</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowProtocol(false)}
                className="text-muted-foreground hover:text-[#1A1A1A]"
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Current Medications (if any) */}
              {assessment.medications && assessment.medications.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Your Current Medications
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {assessment.medications.map((med, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {med}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Schedule Time Slots */}
              <div className="space-y-4">
                {/* Morning */}
                {generatedProtocol.morning && generatedProtocol.morning.length > 0 && (
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Sun className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A1A1A]">Morning</h4>
                        <p className="text-xs text-muted-foreground">With breakfast</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {generatedProtocol.morning.map((item, i) => (
                        <div key={i} className={`flex items-center gap-2 bg-white rounded-lg p-3 ${item.is_medication ? 'border-2 border-blue-300' : 'border border-amber-100'}`}>
                          <Pill className={`w-4 h-4 ${item.is_medication ? 'text-blue-600' : 'text-amber-600'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{item.name}</p>
                              {item.is_medication && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Rx</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{item.dosage}</p>
                          </div>
                          {item.note && (
                            <span className={`text-xs px-2 py-0.5 rounded ${item.is_medication ? 'text-blue-600 bg-blue-100' : 'text-amber-600 bg-amber-100'}`}>
                              {item.note}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Afternoon */}
                {generatedProtocol.afternoon && generatedProtocol.afternoon.length > 0 && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Coffee className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A1A1A]">Afternoon</h4>
                        <p className="text-xs text-muted-foreground">With lunch or 2-4 hours after morning dose</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {generatedProtocol.afternoon.map((item, i) => (
                        <div key={i} className={`flex items-center gap-2 bg-white rounded-lg p-3 ${item.is_medication ? 'border-2 border-blue-300' : 'border border-blue-100'}`}>
                          <Pill className={`w-4 h-4 ${item.is_medication ? 'text-blue-700' : 'text-blue-600'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{item.name}</p>
                              {item.is_medication && (
                                <span className="text-xs bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded font-medium">Rx</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{item.dosage}</p>
                          </div>
                          {item.note && (
                            <span className={`text-xs px-2 py-0.5 rounded ${item.is_medication ? 'text-blue-700 bg-blue-200' : 'text-blue-600 bg-blue-100'}`}>
                              {item.note}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Evening */}
                {generatedProtocol.evening && generatedProtocol.evening.length > 0 && (
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Sunset className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A1A1A]">Evening</h4>
                        <p className="text-xs text-muted-foreground">With dinner</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {generatedProtocol.evening.map((item, i) => (
                        <div key={i} className={`flex items-center gap-2 bg-white rounded-lg p-3 ${item.is_medication ? 'border-2 border-blue-300' : 'border border-purple-100'}`}>
                          <Pill className={`w-4 h-4 ${item.is_medication ? 'text-blue-600' : 'text-purple-600'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{item.name}</p>
                              {item.is_medication && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Rx</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{item.dosage}</p>
                          </div>
                          {item.note && (
                            <span className={`text-xs px-2 py-0.5 rounded ${item.is_medication ? 'text-blue-600 bg-blue-100' : 'text-purple-600 bg-purple-100'}`}>
                              {item.note}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bedtime */}
                {generatedProtocol.bedtime && generatedProtocol.bedtime.length > 0 && (
                  <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Moon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A1A1A]">Bedtime</h4>
                        <p className="text-xs text-muted-foreground">30-60 minutes before sleep</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {generatedProtocol.bedtime.map((item, i) => (
                        <div key={i} className={`flex items-center gap-2 bg-white rounded-lg p-3 ${item.is_medication ? 'border-2 border-blue-300' : 'border border-indigo-100'}`}>
                          <Pill className={`w-4 h-4 ${item.is_medication ? 'text-blue-600' : 'text-indigo-600'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{item.name}</p>
                              {item.is_medication && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Rx</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{item.dosage}</p>
                          </div>
                          {item.note && (
                            <span className={`text-xs px-2 py-0.5 rounded ${item.is_medication ? 'text-blue-600 bg-blue-100' : 'text-indigo-600 bg-indigo-100'}`}>
                              {item.note}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Interaction Alerts */}
              {generatedProtocol.alerts && generatedProtocol.alerts.length > 0 && (
                <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h4 className="font-semibold text-amber-900 flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5" />
                    Important Timing Notes
                  </h4>
                  <ul className="space-y-2">
                    {generatedProtocol.alerts.map((alert, i) => (
                      <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                        <span className="text-amber-500 mt-1">•</span>
                        {alert}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-border bg-muted/30 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={downloadProtocolPdf}
                  disabled={downloadingProtocolPdf}
                >
                  {downloadingProtocolPdf ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.print()}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button
                  variant={protocolSaved ? "default" : "outline"}
                  onClick={saveProtocol}
                  disabled={savingProtocol || protocolSaved}
                  className={protocolSaved ? "bg-green-600 hover:bg-green-600" : ""}
                >
                  {savingProtocol ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : protocolSaved ? (
                    <BookmarkCheck className="w-4 h-4 mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {protocolSaved ? 'Saved!' : 'Save Protocol'}
                </Button>
              </div>
              <Button
                onClick={remindersCreated ? () => navigate('/reminders') : createRemindersFromPlan}
                disabled={creatingReminders}
                className={remindersCreated ? "bg-green-600 hover:bg-green-700" : "btn-primary"}
                data-testid="auto-create-reminders-btn"
              >
                {creatingReminders ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : remindersCreated ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                {remindersCreated ? 'See Your Reminders' : 'Set Up Reminders from Plan'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Current Medications Protocol Modal */}
      {showCurrentMedsProtocol && currentMedsProtocol && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCurrentMedsProtocol(false);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-border flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100">
              <div>
                <h2 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
                  <Clock className="w-6 h-6 text-blue-600" />
                  When to Take Your Current Medications
                </h2>
                <p className="text-muted-foreground mt-1">Optimized timing for maximum effectiveness</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCurrentMedsProtocol(false)}
                className="text-muted-foreground hover:text-[#1A1A1A]"
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* Morning */}
                {currentMedsProtocol.morning?.length > 0 && (
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Sun className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A1A1A]">Morning</h4>
                        <p className="text-xs text-muted-foreground">With breakfast</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {currentMedsProtocol.morning.map((item, i) => (
                        <div key={i} className={`flex items-center gap-2 bg-white rounded-lg p-3 ${item.is_medication ? 'border-2 border-blue-300' : 'border border-amber-100'}`}>
                          <Pill className={`w-4 h-4 ${item.is_medication ? 'text-blue-600' : 'text-amber-600'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{item.name}</p>
                              {item.is_medication && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Rx</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{item.dosage}</p>
                          </div>
                          {item.note && (
                            <span className={`text-xs px-2 py-0.5 rounded ${item.is_medication ? 'text-blue-600 bg-blue-100' : 'text-amber-600 bg-amber-100'}`}>
                              {item.note}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Evening */}
                {currentMedsProtocol.evening?.length > 0 && (
                  <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <Sunset className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A1A1A]">Evening</h4>
                        <p className="text-xs text-muted-foreground">With dinner</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {currentMedsProtocol.evening.map((item, i) => (
                        <div key={i} className={`flex items-center gap-2 bg-white rounded-lg p-3 ${item.is_medication ? 'border-2 border-blue-300' : 'border border-orange-100'}`}>
                          <Pill className={`w-4 h-4 ${item.is_medication ? 'text-blue-600' : 'text-orange-600'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{item.name}</p>
                              {item.is_medication && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Rx</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{item.dosage}</p>
                          </div>
                          {item.note && (
                            <span className={`text-xs px-2 py-0.5 rounded ${item.is_medication ? 'text-blue-600 bg-blue-100' : 'text-orange-600 bg-orange-100'}`}>
                              {item.note}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bedtime */}
                {currentMedsProtocol.bedtime?.length > 0 && (
                  <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Moon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A1A1A]">Bedtime</h4>
                        <p className="text-xs text-muted-foreground">30-60 minutes before sleep</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {currentMedsProtocol.bedtime.map((item, i) => (
                        <div key={i} className={`flex items-center gap-2 bg-white rounded-lg p-3 ${item.is_medication ? 'border-2 border-blue-300' : 'border border-indigo-100'}`}>
                          <Pill className={`w-4 h-4 ${item.is_medication ? 'text-blue-600' : 'text-indigo-600'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{item.name}</p>
                              {item.is_medication && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Rx</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{item.dosage}</p>
                          </div>
                          {item.note && (
                            <span className={`text-xs px-2 py-0.5 rounded ${item.is_medication ? 'text-blue-600 bg-blue-100' : 'text-indigo-600 bg-indigo-100'}`}>
                              {item.note}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timing Notes */}
                {currentMedsProtocol.alerts?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <h4 className="font-semibold text-amber-800">Important Timing Notes</h4>
                    </div>
                    <ul className="space-y-2">
                      {currentMedsProtocol.alerts.map((alert, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                          <span className="text-amber-500 mt-1">•</span>
                          <span>{alert}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-border bg-muted/30 flex justify-end">
              <Button
                variant="outline"
                onClick={() => setShowCurrentMedsProtocol(false)}
              >
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      <GlobalFooter />
    </div>
  );
}
